(ns nosco.richtext.mentions
  "Support for mentions and relevant autocomplete functionality.

  Based on the prosemirror-autocomplete plugin, which uses InputRules
  for triggering a mention sequence.

  The autocmplete plugin is just responsible for converting actions
  into <reducer> calls that is then used to push state to other
  plugins.  The UI re-renders because changing the state of the
  EditorView means re-rendering React components as well. The React
  components pluck out the state they're interested in according to
  their plugin. There's probably some optimisations to be made there.

  There's some limitations that are not fundamental (i.e. can be fixed
  with trivial PRs)

  - only one instance of the plugin at a time, since it hardcodes its
  plugin key

  - TODO investigate if this means only one active instance of PM in a
  page?"
  (:require
   ["prosemirror-autocomplete" :refer [autocomplete closeAutocomplete]]
   ["prosemirror-state" :refer [Plugin PluginKey]]
   ["react" :as React]
   [clojure.string :as string]
   [goog.object :as gobj]
   [hx.hooks :as hooks]
   [hx.react :as hx :refer [defnc]]
   [nosco.graphql-client :as graphql :refer [graphql-query]]
   [nosco.i18n.translate :as t]
   [nosco.ui.avatar :as avatar]
   [nosco.ui.components :as components]
   [nosco.ui.errors :as errors]
   [nosco.ui.loading :as loading]
   [nosco.ui.popup :as popup]
   [nosco.util :as util]
   [reseda.react :as rr :refer [useCachedSuspending]]))

(def search-users (graphql-query "./Mentions.graphql" :SearchUsers))
(def users-for-mention (graphql-query "./Mentions.graphql" :UsersForMention))
(def search-ideas (graphql-query "./Mentions.graphql" :SearchIdeas))

(def ^:private debounced-search-users!
  (util/debounce
   300
   (fn [search]
     (graphql/query-promise!
      search-users
      {:search search}))))

(defn- search-users! [search]
  (-> (debounced-search-users! search)
      (.then (fn [r] (-> r :data :searchUsers :nodes)))
      (errors/suspending-value)))

(defn get-users-for-mention! [idea-id]
  (-> (graphql/query-promise!
       users-for-mention
       {:ideaId idea-id})
      (.then (fn [r] (-> r :data :usersForMention :nodes)))
      (errors/suspending-value)))

(defn search-ideas! [search]
  (-> (graphql/query-promise!
       search-ideas
       {:search (pr-str {:ideas.filter/dimension :title
                         :ideas.filter/operator :search
                         :ideas.filter/value search})})
      (.then (fn [r] (-> r :data :searchIdeas3 :nodes)))
      (errors/suspending-value)))

(defn search-formula-refs! [formula-refs search]
  (-> (for [candidate formula-refs
            :when (string/starts-with? (:title candidate) search)]
        candidate)
      rr/suspending-resolved))

;; the mentions UI is already present in a portal,
;; And it registers its own callbacks with the plugin
(defonce user-mentions-plugin-key (PluginKey. "nosco-suggestion-user"))
(defonce idea-mentions-plugin-key (PluginKey. "nosco-suggestion-idea"))
(defonce formula-refs-mentions-plugin-key (PluginKey. "nosco-suggestion-formula-refs"))

(defnc MentionsLoading []
  [:div {:class "bg-white shadow-sm rounded-sm font-size-sm p-4"
         :style {:width "16rem"}}
   [loading/LoadingNew]])

(defn insert-user-mention [view user range]
  (let [mention-node (gobj/getValueByKeys view "state" "schema" "nodes" "mention")
        mention (.create mention-node #js {:mention (str "user:" (:id user))
                                           :href (str "/users/" (:id user))
                                           :name (str "@" (:fullName user))})
        ;; replace whatever the user has typed with the node TODO perhaps add a space after?
        transaction (-> view .-state .-tr (.replaceWith (.-from range) (.-to range) mention))]
    ;; dispatch the transaction to actually do the replacement
    (.dispatch view transaction)))

(defn insert-idea-mention [view idea range]
  (let [mention-node (gobj/getValueByKeys view "state" "schema" "nodes" "mention")
        mention (.create mention-node #js {:mention (str "idea:" (:id idea))
                                           :href (str "/ideas/" (:id idea))
                                           :name (str "#" (:title idea))})
        ;; replace whatever the user has typed with the node TODO perhaps add a space after?
        transaction (-> view .-state .-tr (.replaceWith (.-from range) (.-to range) mention))]
    ;; dispatch the transaction to actually do the replacement
    (.dispatch view transaction)))

(defn insert-formula-refs-mention [view formula-ref range]
  (let [formula-ref-node (gobj/getValueByKeys view "state" "schema" "nodes" "formula_ref")
        formula-ref (.create formula-ref-node #js {:formula-ref (:id formula-ref)
                                                   :title (str "#" (:title formula-ref))})
        ;; replace whatever the user has typed with the node TODO perhaps add a space after?
        transaction (-> view .-state .-tr (.replaceWith (.-from range) (.-to range) formula-ref))]
    ;; dispatch the transaction to actually do the replacement
    (.dispatch view transaction)))

(defnc UserMentionsResultsPane [{:keys [items active-item on-select]}]
  [:<>
   (when-not (seq items)
     [:div {:class "p-3"}
      (t/t "No users found")])
   (for [user items]
     [:button {:class ["button-reset d-flex p-2 w-100 nosco-mention"]
               :role "option"
               :aria-selected (= user active-item)
               :on-click #(on-select user)}
      [avatar/Avatar {:user user :size :sm}]
      [components/Spacer {:class "mr-2"}]
      [:div {:class "nosco-mention__user-name"}
       (:fullName user)]])])

(defnc IdeaMentionsResultsPane [{:keys [items active-item on-select]}]
  [:<>
   (when-not (seq items)
     [:div {:class "p-3"}
      (t/t "No ideas found")])
   (for [idea items]
     [:button {:class ["button-reset d-flex justify-content-start text-left p-2 w-100 nosco-mention"]
               :role "option"
               :aria-selected (= idea active-item)
               :on-click #(on-select idea)}
      (:title idea)])])

(defnc FormulaRefsMentionsResultsPane [{:keys [items active-item on-select]}]
  [:<>
   (when-not (seq items)
     [:div {:class "p-3"}
      (t/t "No numeric field found")])
   (for [formula-ref items]
     [:button {:class ["button-reset d-flex justify-content-start text-left p-2 w-100 nosco-mention"]
               :role "option"
               :aria-selected (= (:id formula-ref) (:id active-item))
               :on-click #(on-select formula-ref)}
      (:title formula-ref)])])

(defn clamp-active-idx [state]
  (let [{:keys [items* active-idx]} state]
    (if-not (some-> items* realized?)
      (assoc state :active-idx 0)
      (let [items (deref items*)
            clamped (util/clamp active-idx 0 (dec (count items)))]
        (assoc state :active-idx clamped)))))

(defn user-mentions-plugin []
  (Plugin. #js {:key user-mentions-plugin-key
                :state #js {:init
                            (fn []
                              {:showing false
                               :active-idx nil
                               :items* nil})
                            :apply (fn [^js tr state]
                                     (this-as this
                                       (if-let [meta (.getMeta tr this)]
                                         (-> state
                                             (merge meta)
                                             (clamp-active-idx))
                                         state)))}}))

(defn idea-mentions-plugin []
  (Plugin. #js {:key idea-mentions-plugin-key
                :state #js {:init
                            (fn []
                              {:showing false
                               :active-idx nil
                               :items* nil})
                            :apply (fn [^js tr state]
                                     (this-as this
                                       (if-let [meta (.getMeta tr this)]
                                         (-> state
                                             (merge meta)
                                             (clamp-active-idx))
                                         state)))}}))

(defn formula-refs-mentions-plugin []
  (Plugin. #js {:key formula-refs-mentions-plugin-key
                :state #js {:init
                            (fn []
                              ()
                              {:showing false
                               :active-idx nil
                               :items* nil})
                            :apply (fn [^js tr state]
                                     (this-as this
                                       (if-let [meta (.getMeta tr this)]
                                         (-> state
                                             (merge meta)
                                             (clamp-active-idx))
                                         state)))}}))

(defn- active-item-maybe [items* active-idx]
  (when (and items* (realized? items*))
    (let [items (deref items*)]
      (nth items active-idx nil))))

(defnc MentionsResults [{:keys [items* init? active-idx on-select results-component]}]
  (let [items (rr/deref* items*)
        active-item (active-item-maybe items* active-idx)]
    (if (or (nil? items)
            (and (empty? items) init?))
      [MentionsLoading]
      [:div {:class "bg-white shadow-sm rounded-sm font-size-sm"
             :style {:width "16rem"}}
       [results-component {:items items
                           :active-item active-item
                           :on-select on-select}]])))

(defn dispatch-meta [plugin-key ^js view new-state]
  (let [plugin (.get plugin-key (.-state view))
        tr (.. view -state -tr)
        tr (.setMeta tr plugin new-state)]
    (.dispatch view tr)))

;; these two FooMentionPopup components are almost exactly the same but I
;; didn't bother unifying them, they rely on at least 4 different props...

(defnc UserMentionsPopup [{:keys [view state]}]
  (let [plugin-state (.getState user-mentions-plugin-key state)
        {:keys [showing items* init? active-idx range]} plugin-state
        node (js/document.getElementById "user-mentions-decoration-id")
        [popper-el set-popper-el] (hooks/useState nil)
        on-select (fn [item]
                    (insert-user-mention view item range)
                    (closeAutocomplete view))
        [items* _loading] (useCachedSuspending items*)
        ;; use popper to align the search results popup with the Decorated node
        popper (popup/use-popper-native
                node
                popper-el
                (popup/popper-options
                 {:placement "bottom-start"
                  :flip true
                  :offset [0 8]}))]
    (hooks/useEffect
     (fn []
       (when (.-update popper)
         (.update popper)))
     [items*])
    [popup/Overlay
     {:popper (popup/popper-for-overlay popper set-popper-el)
      :open showing
      :lock-focus false}
     [:div
      [React/Suspense {:fallback (hx/f [MentionsLoading])}
       [MentionsResults
        {:items* items*
         :init? init?
         :active-idx active-idx
         :results-component UserMentionsResultsPane
         :on-select on-select}]]]]))

(defnc IdeaMentionsPopup [{:keys [view state]}]
  (let [plugin-state (.getState idea-mentions-plugin-key state)
        {:keys [showing items* active-idx range]} plugin-state
        node (js/document.getElementById "idea-mentions-decoration-id")
        [popper-el set-popper-el] (hooks/useState nil)
        on-select (fn [item]
                    (insert-idea-mention view item range)
                    (closeAutocomplete view))
        [items* _loading] (useCachedSuspending items*)
        ;; use popper to align the search results popup with the Decorated node
        popper (popup/use-popper-native
                node
                popper-el
                (popup/popper-options
                 {:placement "bottom-start"
                  :flip true
                  :offset [0 8]}))]
    (hooks/useEffect
     (fn []
       (when (.-update popper)
         (.update popper)))
     [items*])
    [popup/Overlay
     {:popper (popup/popper-for-overlay popper set-popper-el)
      :open showing
      :lock-focus false}
     [:div
      [React/Suspense {:fallback (hx/f [MentionsLoading])}
       [MentionsResults
        {:items* items*
         :active-idx active-idx
         :results-component IdeaMentionsResultsPane
         :on-select on-select}]]]]))

(defnc FormulaRefsMentionsPopup [{:keys [view state]}]
  (let [plugin-state (.getState formula-refs-mentions-plugin-key state)
        {:keys [showing items* active-idx range]} plugin-state
        node (js/document.getElementById "formula-refs-mentions-decoration-id")
        [popper-el set-popper-el] (hooks/useState nil)
        on-select (fn [item]
                    (insert-formula-refs-mention view item range)
                    (closeAutocomplete view))
        ;; use popper to align the search results popup with the Decorated node
        popper (popup/use-popper-native node popper-el
                                        (popup/popper-options {:placement "bottom-start"
                                                               :flip true}))]
    [popup/Overlay {:popper (popup/popper-for-overlay popper set-popper-el)
                    :open showing
                    :lock-focus false}
     [:div
      [React/Suspense {:fallback (hx/f [MentionsLoading])}
       [MentionsResults {:items* items*
                         :active-idx active-idx
                         :results-component FormulaRefsMentionsResultsPane
                         :on-select on-select}]]]]))

(defn- autocomplete-reducer
  ([^js action]
   (autocomplete-reducer nil action))
  ([opts ^js action]
   (let [view (.-view action)
         kind (.-kind action)
         type (-> action .-type .-name)

         {:keys [plugin-key insert search init]}
         (case type
           "mention"
           {:plugin-key user-mentions-plugin-key
            :insert insert-user-mention
            :search search-users!
            :init (when (and (= kind "open") (:idea-id opts))
                    (get-users-for-mention! (:idea-id opts)))}

           "idea"
           {:plugin-key idea-mentions-plugin-key
            :insert insert-idea-mention
            :search search-ideas!
            :init nil}

           "formulaRefs"
           {:plugin-key formula-refs-mentions-plugin-key
            :insert insert-formula-refs-mention
            :search (partial search-formula-refs! (:formula-refs opts))
            :init (rr/suspending-resolved (:formula-refs opts))})

         plugin-state (.getState plugin-key (.-state view))]
     (case kind
       "open"
       (do
         (dispatch-meta plugin-key view {:showing true
                                         :items* init
                                         :init? true
                                         :range (.-range action)})
         true)

       "close"
       (do
         (dispatch-meta plugin-key view {:showing false
                                         :range nil
                                         :items* init
                                         :init? true})
         true)

       "filter"
       (do
         (dispatch-meta plugin-key view {:items* (search (.-filter action))
                                         :init? false
                                         :range (.-range action)
                                         :active-idx 0})
         true)

       "ArrowDown"
       (do
         (dispatch-meta plugin-key view (update plugin-state :active-idx inc))
         true)

       "ArrowUp"
       (do
         (dispatch-meta plugin-key view (update plugin-state :active-idx dec))
         true)

       ("ArrowLeft" "ArrowRight")
       true

       "enter"
       (let [{:keys [active-idx items*]} plugin-state]
         (when-let [item (active-item-maybe items* active-idx)]
           (insert view item (.-range action))
           (closeAutocomplete view))
         true)

       ;; default
       false))))

(defn- user-options [idea-id]
  #js {:triggers
       #js [#js {:name "mention"
                 :trigger "@"
                 :decorationAttrs #js {:id "user-mentions-decoration-id"}
                 :cancelOnFirstSpace false}
            #js {:name "idea"
                 :trigger "#"
                 :decorationAttrs #js {:id "idea-mentions-decoration-id"}
                 :cancelOnFirstSpace false}]
       :reducer (partial autocomplete-reducer {:idea-id idea-id})})

(defn plugins [idea-id]
  (.concat (autocomplete (user-options idea-id))
           #js[(user-mentions-plugin) (idea-mentions-plugin)]))

;; Used by the submission form "calculated number" fields
(defn formula-plugins [formula-refs]
  (.concat
   (autocomplete
    #js {:triggers
         #js [#js {:name "formulaRefs"
                   :trigger "#"
                   :decorationAttrs #js {:id "formula-refs-mentions-decoration-id"}
                   :cancelOnFirstSpace true}]
         :reducer (partial autocomplete-reducer {:formula-refs formula-refs})})
   #js[(formula-refs-mentions-plugin)]))
